/**
 * When a styled text cell becomes a formula cell, its rendering switches from
 * `cell.content` (text path) to `cell.calcFormatHtml` (calc path — see
 * render-calc-formatting.ts). If `calcFormatHtml` is absent the calc path emits
 * a bare `<p>value</p>`, so the cell's prior text styling (font size, color,
 * bold/italic/underline, alignment, …) visibly disappears.
 *
 * This derives a calc-format seed from the cell's existing styled content: the
 * same block + inline wrappers, but collapsed to a SINGLE placeholder text node
 * so renderCalcResult can inject the computed value into it. Only the first
 * text run's styling is preserved — the calc result is a single value, so any
 * additional runs/blocks are intentionally dropped (keeping them would leave
 * stray leftover text rendering next to the value).
 *
 * Returns undefined when there is no styled text to preserve (empty cell, or a
 * content with no real text run like `<p><br></p>`), in which case the caller
 * leaves `calcFormatHtml` unset and the plain fallback applies.
 */
export const seedCalcFormatFromContent = (
  contentHtml?: string,
): string | undefined => {
  const html = contentHtml?.trim();
  if (!html) return undefined;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const block = doc.body.firstElementChild;
  if (!block) return undefined;

  // First non-whitespace text node inside the block = the run whose styling we
  // keep (mirrors how renderCalcResult picks the injection point).
  const walker = doc.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let textNode: Node | null = walker.nextNode();
  while (textNode && !textNode.nodeValue?.trim()) {
    textNode = walker.nextNode();
  }
  if (!textNode) return undefined;

  // Prune every sibling along the path from the text node up to the block, so
  // only the styled chain wrapping that first run survives (drops other inline
  // runs). block.outerHTML then drops any sibling blocks.
  let node: Node = textNode;
  while (node !== block && node.parentNode) {
    const parent = node.parentNode;
    for (const sibling of Array.from(parent.childNodes)) {
      if (sibling !== node) parent.removeChild(sibling);
    }
    node = parent;
  }
  textNode.nodeValue = "VALUE";

  return block.outerHTML;
};
